// sql.js library
// import initSqlJs from "sql.js";

// pgsql library
import { PGlite } from '@electric-sql/pglite';

// const sqlJs: Promise<initSqlJs.SqlJsStatic> = initSqlJs();

// Local
import { Fail, Source, getFilenameExtension, getFilenameWithoutExtension, Success, Named, assert } from './util';
// import { Schema, TableInfo, extractTableInfo } from './schema';
// import { S } from '@electric-sql/pglite/dist/pglite-CntadC_p';


let wasmModule: WebAssembly.Module | null = null;

// avoids error, when StaticDb is called multiple times
async function getWasmModule() {
  if (!wasmModule) {
    const response = await fetch("/res/pglite.wasm");
    const bytes = await response.arrayBuffer();
    wasmModule = await WebAssembly.compile(bytes);
  }
  return wasmModule;
}

/////////////////////////////////////////
// Interface for statically loaded dbs //
/////////////////////////////////////////

export type DbSource = { type: 'initial-sql-script', source: Source<string> } |
                       { type: 'sqlite-db',          source: Source<Uint8Array> }
export type DbData = { type: 'initial-sql-script', sql:  string } |
                     { type: 'sqlite-db',          data: Uint8Array }

export type FetchDbFail       = { kind: 'fetch-db', url: string }

export type RunInitScriptFail = { kind: 'run-init-script', details: string }
export type ReadSqliteDbFail  = { kind: 'read-sqlite-db', details: string }
export type InitDbFail        = RunInitScriptFail | ReadSqliteDbFail

export type ParseSchemaFail   = { kind: 'parse-schema', details: string };

/**
 * Terminology:
 * - "resolved" means that the `db` promise is resolved
 */
export class StaticDb {
    
    //private db: Promise<Success<initSqlJs.Database> | Fail<InitDbFail>>;

    private db: Promise<Success<PGlite> | Fail<InitDbFail>>;

    /*
    constructor(readonly source: DbData | null) {
        this.db = sqlJs.then(SQL => {
            if (this.source === null) {
                const db = new SQL.Database();
                
                // Success: Empty db
                return { ok: true, data: db };
            }
            else if (this.source.type === 'initial-sql-script') {
                const db = new SQL.Database();
                try {
                    db.run(this.source.sql);
                    
                    // Success: Initial SQL script succeeded
                    return { ok: true, data: db };
                }
                catch (e) {
                    // Failure: Initial SQL script failed
                    return { ok: false, error: { kind: 'run-init-script', details: String(e) } };
                }
            }
            else {
                try {
                    const db = new SQL.Database(this.source.data);

                    // We need to manually check whether the db actually works
                    const res = db.exec(`SELECT name FROM sqlite_master WHERE type='table'`);

                    if (res.length !== 1) {
                        return { ok: false, error: { kind: 'read-sqlite-db', details: 'Could not read from db' } };
                    }
    
                    // Success: SQLite db read succeeded
                    return { ok: true, data: db };
                }
                catch (e) {
                    // Failure: SQLite db read failed
                    return { ok: false, error: { kind: 'read-sqlite-db', details: String(e) } };
                }
            }
        });
    }
    */

    constructor(readonly source: DbData | null) {
      // console.log("constructor StaticDB called: ", source);
      this.db = this.initDb(source);
    }

    private async initDb(source: DbData | null): Promise<Success<PGlite> | Fail<InitDbFail>> {
      
      try {
        
        const module = await getWasmModule();

        if (this.source === null) {

              const db = new PGlite({wasmModule: module});

              // Success: empty database
              return { ok: true, data: db };
        }

        else if (this.source.type === 'initial-sql-script') {

            const db = new PGlite({wasmModule: module});
            try {

                // must split script to statements
                const statements = this.splitSqlStatements(this.source.sql);
                for (const stmt of statements) {
                  await db.query(stmt);
                }

                // Success: database loaded
                return { ok: true, data: db };
            }
            catch (e) {

                // Failure: Initial SQL script failed
                return { ok: false, error: { kind: 'run-init-script', details: String(e) } };
            }
        }


        // const db = new PGlite();
        return { ok: false, error: { kind: 'run-init-script', details: 'bla bli' }};

        //return { ok: true, data: db };
      } catch (err: unknown) {
        // Fehler wird typisiert in InitDbFail
        return { ok: false, error: { kind: 'run-init-script', details: (err as Error).message } };
      }
    }

    private splitSqlStatements(sqlScript: string): string[] {
      // 1. Entferne Zeilenkommentare -- und /**/ falls nötig
      const cleaned = sqlScript
        .replace(/--.*$/gm, '')       // Einzeilige Kommentare
        .replace(/\/\*[\s\S]*?\*\//g, ''); // Block-Kommentare

      // 2. Splitte bei Semikolon
      const statements = cleaned
        .split(';')
        .map(stmt => stmt.trim())     // Leerzeichen entfernen
        .filter(stmt => stmt.length > 0); // Leere Statements entfernen

      return statements;
    }

    

    /*
    async exec(sql: string): Promise<Success<SqlResult> | Fail<InitDbFail>> {
        // Forward potential failure
        return this.db.then((dbRes) => {
            if (!dbRes.ok) {
                return dbRes;
            }
            const db = dbRes.data;

            // Query db
            let result: SqlResult;
            try {
                const results = db.exec(sql);
                result = { type: 'succ', timestamp: new Date(), sql, result: results };
            }
            catch (e: any) {
                result = { type: 'error', timestamp: new Date(), sql, message: e.toString() };
            }

            return { ok: true, data: result };
        });
    }
    */

  async exec(sql: string): Promise<Success<SqlResult> | Fail<InitDbFail>> {

    // console.log("exec called:", sql);

    const dbRes = await this.db;
    if (!dbRes.ok) {
      return dbRes; 
    }

    const db = dbRes.data;
    let result: SqlResult;

    try {

      const queryResult = await db.query(sql);
      // console.log(queryResult);

      const mapped: QueryResult = {   // make a copy 
        fields: queryResult.fields,
        rows: queryResult.rows,
        affectedRows: queryResult.affectedRows
      };

      //console.log(mapped);

      // convert DATE and TIMESTAMP to formated Strings
      mapped.rows.forEach(row => {
        mapped.fields.forEach(field => {
          switch (field.dataTypeID) {

            //   case 1083: // TIME is ok
            //       row[field.name] = convertTime(row[field.name]);
            //      break;

              case 1082: // DATE
                row[field.name] = this.formatDate(row[field.name]);
                break;

              case 1114: // TIMESTAMP
                row[field.name] = this.formatTimeStamp(row[field.name]);
                break;
          } 
        });
      });

      result = { type: 'succ', timestamp: new Date(), sql, result: mapped };

    } catch (e: any) {
      result = { type: 'error', timestamp: new Date(), sql, message: e.toString() };
    }

    return { ok: true, data: result };
  }

  private formatDate(date: Date): String {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private formatTimeStamp(date: Date): String {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    const ms = String(date.getMilliseconds()).padStart(3, "0");

    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}.${ms}`;
  }


    /*
    async querySchema(): Promise<Success<Schema> | Fail<InitDbFail | ParseSchemaFail>> {
        const resultsRes = await this.exec('SELECT sql FROM sqlite_schema WHERE type=\'table\'');
        // Forward potential failure
        if (!resultsRes.ok) {
            return resultsRes;
        }
        const results = resultsRes.data;

        if (results.type === 'error') {
            return { ok: false, error: { kind: 'parse-schema', details: 'Could not retrieve db infos' } };
        }

        assert(results.result.length <= 1, 'Expected at most one result');

        const tableInfos: TableInfo[] = [];

        if (results.result.length === 1) {    
            const createTableStmts = results.result[0].values;
            for (const createTableStmt of createTableStmts)
            {
                if (createTableStmt.length !== 1 || typeof createTableStmt[0] !== 'string') {
                    return { ok: false, error: { kind: 'parse-schema', details: 'Could not retrieve table infos' } };
                }

                const sql = createTableStmt[0];
                const tableInfoResult = extractTableInfo(sql);

                // Failure during extraction?
                if (!tableInfoResult.ok) {
                    // Forward
                    if (tableInfoResult.error.kind === 'extraction') {
                        return { ok: false, error: { kind: 'parse-schema', details: 'Fail during extraction: ' + tableInfoResult.error.details } };
                    }
                    // On "internal-table" fail: This table is to be simply ignored
                }
                else {
                    const tableInfo = tableInfoResult.data;
                    tableInfos.push(tableInfo);
                }
            }
        }

        return { ok: true, data: tableInfos };
    }
    */

  async querySchema(): Promise<Success<SchemaQueryResult> | Fail<InitDbFail | ParseSchemaFail>> {  

    // console.log('querySchema()')

    // fetch table names
    const sql =  `SELECT table_name FROM information_schema.tables 
      WHERE table_schema = 'public' AND table_type = 'BASE TABLE'` 

    const resultsRes = await this.exec(sql);
    if (!resultsRes.ok) {
      return resultsRes;
    }

    const results = resultsRes.data as SqlResultSucc;  // cast needed
    const rows = results.result.rows; // rows contains tabelle names: { table_name: 'getraenke' }

    const schema: SchemaQueryResult = { tables: [] };   // create empty schema

    rows.forEach(row => {
      const tableName = (row as { table_name: string }).table_name;   // cast needed 
      // console.log(tableName);
      const table: SchemaTable = { name: tableName, columns: [] };
      schema.tables.push(table);
    });

    // fetch column names
    for (const table of schema.tables) {
      const sql = `SELECT column_name FROM information_schema.columns
             WHERE table_name = '${table.name}' AND table_schema = 'public' ORDER BY ordinal_position;`;

      const resultsRes = await this.exec(sql);
      if (!resultsRes.ok) {
        return resultsRes;
      }

      const results = resultsRes.data as SqlResultSucc;  // cast
      const rows = results.result.rows;

      rows.forEach(row => {
        const columnName = (row as { column_name: string }).column_name;
        //console.log(columnName);
        const column: SchemaColumn = { name: columnName, primary_key: false, foreign_key: false };
        table.columns.push(column);
      });

    }

    // fetch primary key
    for (const table of schema.tables) {
      for (const column of table.columns) {

        const sql = `
              SELECT EXISTS (
                SELECT 1
                FROM information_schema.table_constraints tc
                JOIN information_schema.key_column_usage kcu
                  ON tc.constraint_name = kcu.constraint_name
                AND tc.table_name = kcu.table_name
                WHERE tc.table_name = '${table.name}'
                  AND kcu.column_name = '${column.name}'
                  AND tc.table_schema = 'public'
                  AND tc.constraint_type = 'PRIMARY KEY'
              );
            `;

        const results = await this.exec(sql);
        if (!results.ok) {
          return results;
        }

        const sqlResults = results.data as SqlResultSucc;
        const rows = sqlResults.result.rows;
        // console.log('fetch pk', rows[0]);
        column.primary_key = (rows[0] as { exists: boolean }).exists;

      }
    }

    // fetch foreign key
    for (const table of schema.tables) {
      for (const column of table.columns) {

        const sql = `
              SELECT EXISTS (
                SELECT 1
                FROM information_schema.table_constraints tc
                JOIN information_schema.key_column_usage kcu
                  ON tc.constraint_name = kcu.constraint_name
                AND tc.table_name = kcu.table_name
                WHERE tc.table_name = '${table.name}'
                  AND kcu.column_name = '${column.name}'
                  AND tc.table_schema = 'public'
                  AND tc.constraint_type = 'FOREIGN KEY'
              );
            `;

        const results = await this.exec(sql);
        if (!results.ok) {
          return results;
        }

        const sqlResults = results.data as SqlResultSucc;
        const rows = sqlResults.result.rows;
        column.foreign_key = (rows[0] as { exists: boolean }).exists;
      }
    }

    // console.log(schema);
    return { ok: true, data: schema }

  }


    /////////////////////////
    // Explicit resolution //
    /////////////////////////

    async resolve(): Promise<Success<void> | Fail<InitDbFail>> {
        return this.db.then((res) => {
            if (res.ok) {
                return { ok: true, data: undefined };
            }
            else {
                return res;
            }
        });
    }
}

export function makeNamedDbSource(url: string): Named<DbSource> {
    const extension = getFilenameExtension(url);
    const name      = getFilenameWithoutExtension(url);

    if (extension === 'sql') {
        return { type: 'initial-sql-script', source: { type: 'fetch', url }, name };
    }
    else {
        return { type: 'sqlite-db', source: { type: 'fetch', url }, name };
    }
}


//////////////////////
// SQL result types //
//////////////////////

export type SchemaColumn = {
  name: string;
  primary_key: boolean;
  foreign_key: boolean;
};

export type SchemaTable = {
  name: string;
  columns: SchemaColumn[];
};

export type SchemaQueryResult = {
  tables: SchemaTable[];
};

// type does not work
export interface QueryResult<T = any> {
  fields: { name: string; dataTypeID: number} [];
  rows: T[];
  affectedRows?: number;
}

// export type SqlResultSucc   = { type: 'succ',  timestamp: Date, sql: string, result: initSqlJs.QueryExecResult[] }
export type SqlResultSucc   = { type: 'succ',  timestamp: Date, sql: string, result: QueryResult }
export type SqlResultError  = { type: 'error', timestamp: Date, sql: string, message: string }
export type SqlResult = SqlResultSucc | SqlResultError 

export function sqlResultHash(r: SqlResult): string {
    return r.timestamp.getTime() + r.sql;
}
